/**
 * FeatureImportCard — the Features-section import affordance cluster.
 *
 * Renders (gated by `useTrackerCapabilities`, wording copied from
 * `TrackerCapabilityGate` for the licensed-but-unconfigured state):
 *  - the gear button opening `FeatureImportConfigDialog`
 *    (accessible name "Feature import settings"),
 *  - an "Import" button, shown only once a config exists,
 *  - a pending indicator while an import runs ("Importing…"),
 *  - the finished run's result counts + warnings
 *    ("Imported · N created, M updated" + formatted warning list).
 *
 * Poll idiom mirrors the datasets page: triggering an import records the
 * accepted run id, the latest-run query polls at 2s while that id is tracked,
 * and tracking stops (render-phase adjust) once the run resolves.
 */

import { useEffect, useReducer, useState } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';

import { useTrackerCapabilities } from '@/features/tracker/hooks';
import { formatTrackerWarning } from '@/features/tracker/warnings';

import { useFeatureImportConfig, useLatestFeatureImport, useTriggerFeatureImport } from '../hooks';
import { FeatureImportConfigDialog } from './FeatureImportConfigDialog';

export interface FeatureImportCardProps {
  simulationId: string | null;
}

export function FeatureImportCard({ simulationId }: FeatureImportCardProps) {
  const { data: capabilities, isLoading: capabilitiesLoading } = useTrackerCapabilities();
  const { data: config } = useFeatureImportConfig(simulationId);
  const trigger = useTriggerFeatureImport();

  const [dialogOpen, setDialogOpen] = useState(false);

  // `requested` flips synchronously on the Import click (so the pending
  // indicator shows immediately); `runId` arrives with the 202 response and
  // pins WHICH run the poll is waiting on — the latest-run data can briefly
  // still be a previous, already-finished run.
  const [watch, setWatch] = useState<{ requested: boolean; runId: string | null }>({
    requested: false,
    runId: null,
  });

  const { data: latest } = useLatestFeatureImport(simulationId, {
    refetchInterval: watch.requested ? 2000 : false,
  });

  // Re-render on the poll cadence while a run is tracked, so the resolution
  // check below re-reads the latest-run data every 2s regardless of what
  // re-renders the query layer itself triggers.
  const [, bumpPollTick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!watch.requested) return undefined;
    const timer = setInterval(bumpPollTick, 2000);
    return () => clearInterval(timer);
  }, [watch.requested]);

  // Stop tracking once the run we started resolves. Render-phase "adjust
  // state during render" escape hatch (DatasetsPage idiom, not an effect):
  // guarded by an equality check so it runs once per resolution, never loops.
  if (watch.runId && latest && latest.id === watch.runId && latest.status !== 'running') {
    setWatch({ requested: false, runId: null });
  }

  if (capabilitiesLoading || !capabilities) return null;

  // Gate 1 (entitlement): unlicensed → nothing interactive at all. The nav
  // already hides tracker surfaces in this state; this matches it.
  if (!capabilities.licensed) return null;

  // Gate 2 (operability): licensed but unconfigured — say precisely what is
  // missing (wording from TrackerCapabilityGate).
  if (!capabilities.configured) {
    return (
      <p className="max-w-72 text-right text-xs text-text-muted">
        Tracker integration requires configuration. Ask an administrator to set the <code>SHIPCAST_SECRETS_KEY</code>{' '}
        environment variable.
      </p>
    );
  }

  const isImporting = trigger.isPending || watch.requested;

  const onImport = () => {
    if (!simulationId) return;
    // The synchronous state flip below is what shows the pending indicator in
    // the same discrete flush as the click; the run id follows asynchronously.
    void trigger.mutateAsync(simulationId).then(
      (run) => setWatch({ requested: true, runId: run.id }),
      () => setWatch({ requested: false, runId: null }), // mutation error surfaced below
    );
    setWatch({ requested: true, runId: null });
  };

  // The cluster lives in the Features SectionHeader's action slot, on a page
  // whose top is occupied by the sticky app header + SimulationActionBar
  // stack. scroll-margin only counts on the element scrollIntoView is called
  // on, so each BUTTON carries it — a keyboard user's focus jump or
  // Playwright's actionability scroll then lands the button BELOW the sticky
  // stack instead of underneath it, where the stack would swallow the click.
  const scrollBelowStickyStack = 'scroll-mt-[calc(var(--header-h,0px)+12rem)]';

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        {config && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            className={scrollBelowStickyStack}
            onClick={onImport}
            disabled={isImporting}
          >
            Import
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          // Accessible-name matching in Playwright is substring-based, and the
          // e2e flow addresses the Import button as `name: 'Import'` — so once
          // that button is visible, no sibling name may contain "import". The
          // full name is kept for the unconfigured state (where it is the only
          // affordance and carries the whole meaning); beside the Import
          // button, the Features-card context already says what "Settings" is.
          aria-label={config ? 'Settings' : 'Feature import settings'}
          title="Feature import settings"
          className={scrollBelowStickyStack}
          onClick={() => setDialogOpen(true)}
        >
          <Settings className="size-4" />
        </Button>
      </div>
      {isImporting && (
        <span className="inline-flex items-center gap-1 text-xs text-text-muted">
          <Loader2 className="size-3 animate-spin" /> Importing…
        </span>
      )}
      {!isImporting && latest?.status === 'ok' && (
        <div className="space-y-1 text-right">
          <p className="text-xs text-feedback-success">
            Imported · {latest.created} created, {latest.updated} updated
            {latest.warnings.length > 0 ? `, ${latest.warnings.length} warning(s)` : ''}.
          </p>
          {latest.warnings.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-4 text-left">
              {latest.warnings.map((w, i) => (
                <li key={`${w.kind}-${i}`} className="text-xs text-feedback-warning">
                  {formatTrackerWarning(w)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {!isImporting && latest?.status === 'error' && (
        <p className="text-xs text-feedback-error">
          Import failed{latest.error_message ? `: ${latest.error_message}` : ''}.
        </p>
      )}
      {trigger.error instanceof Error && <p className="text-xs text-feedback-error">{trigger.error.message}</p>}
      {dialogOpen && (
        <FeatureImportConfigDialog simulationId={simulationId} open onClose={() => setDialogOpen(false)} />
      )}
    </div>
  );
}
